import { Box, Fade, Paper, styled, Typography } from '@mui/material';
import ErrorIcon from '@acorn-portal/shared/src/assets/shared/icons/base/broken-down-car.svg?react';
import { FC } from 'react';
import extractBeforeDash from '@acorn-portal/shared/src/utils/extractBeforeDash';
import { getEnvProduct } from '@acorn-portal/shared/src/utils/get-env-product';
import { getPhoneNumber } from '@acorn-portal/shared/src/utils/get-phone-numbers';

const ImageWrapper = styled('div')(({ theme }) => ({
	'& svg': {
		height: 'auto',
		width: '12em',
		margin: '1em 0',
		fill: theme.palette.secondary.main,
		[theme.breakpoints.down('md')]: {
			maxWidth: '220px',
		},
	},
}));

const getContactLink = (company: string) => {
	switch (company) {
		case 'CARROT': {
			return 'https://www.carrotinsurance.com/contact-us/';
		}

		case 'STREETCOVER': {
			return 'https://streetcover.co.uk/contact-us/';
		}

		default: {
			return 'https://acorninsure.co.uk/contact-us/';
		}
	}
};

const getContent = (company: string, contactNumber: string) => {
	let content = (
		<>
			<Typography
				variant='body1'
				sx={(theme) => ({
					m: 2,
					[theme.breakpoints.down('md')]: { m: 'unset' },
				})}
			>
				It appears that you currently have no insurance policies available at this time. If you believe this to be an
				error, please don&apos;t hesitate to reach out to our support team for assistance.
			</Typography>
			<Typography
				variant='body1'
				sx={(theme) => ({
					m: 2,
					[theme.breakpoints.down('md')]: { m: 'unset', mt: 2 },
				})}
			>
				Our friendly support team is available to assist you. You can reach us by calling{' '}
				<Typography component='a' href={`tel:${contactNumber}`} color='primary'>
					{contactNumber}
				</Typography>{' '}
				or contact us{' '}
				<Typography component='a' color='primary' href={getContactLink(company)} target='_blank'>
					here.
				</Typography>
				We look forward to hearing from you and helping in any way we can.
			</Typography>
		</>
	);

	if (company === 'MOTORCADE') {
		content = (
			<>
				<Typography
					variant='body1'
					sx={(theme) => ({
						m: 2,
						[theme.breakpoints.down('md')]: { m: 'unset' },
					})}
				>
					It appears that you currently have no insurance policies available at this time. If you believe this to be an
					error, please don&apos;t hesitate to reach out to our support team for assistance.
				</Typography>
				<Typography
					variant='body1'
					sx={(theme) => ({
						m: 2,
						[theme.breakpoints.down('md')]: { m: 'unset', mt: 2 },
					})}
				>
					Our friendly support team is available to assist you. You can contact us{' '}
					<Typography component='a' color='primary' href='https://motorcade.co.uk/contact-us/' target='_blank'>
						here.
					</Typography>
					We look forward to hearing from you and helping in any way we can.
				</Typography>
			</>
		);
	}

	if (company === 'FLAG') {
		content = (
			<>
				<Typography
					variant='body1'
					sx={(theme) => ({
						m: 2,
						[theme.breakpoints.down('md')]: { m: 'unset' },
					})}
				>
					It appears that you currently have no insurance policies available at this time. If you believe this to be an
					error, please don&apos;t hesitate to reach out to our support team for assistance.
				</Typography>
				<Typography
					variant='body1'
					sx={(theme) => ({
						m: 2,
						[theme.breakpoints.down('md')]: { m: 'unset', mt: 2 },
					})}
				>
					Our friendly support team is available to assist you. You can contact us{' '}
					<Typography
						component='a'
						color='primary'
						href='https://www.flaginsurance.co.uk/help-and-support'
						target='_blank'
					>
						here.
					</Typography>
					We look forward to hearing from you and helping in any way we can.
				</Typography>
			</>
		);
	}

	return content;
};

const NoPolicyDetailsCopy: FC = () => {
	const product = getEnvProduct();
	const contactNumber: string = getPhoneNumber(product, 'CONTACT');

	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
			}}
		>
			<Fade in>
				<Box
					sx={{
						margin: { xs: 2, md: '2rem' },
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<Box alignContent='center' textAlign='center' sx={{ m: 4 }}>
						<Typography variant='h4' color='primary'>
							We&apos;re unable to retrieve your policy details
						</Typography>
						<ImageWrapper>
							<ErrorIcon />
						</ImageWrapper>
						{getContent(extractBeforeDash(import.meta.env.VITE_JOURNEY || ''), contactNumber)}
					</Box>
				</Box>
			</Fade>
		</Paper>
	);
};

export default NoPolicyDetailsCopy;
